import type { Localized } from "../i18n";
import type { AgeBand } from "../types/prisma-enums";
import type { GameContent, GameId } from "../curriculum";

/**
 * The five games' display names, and the band labels the sidebar groups by.
 *
 * 🔴 WHY THIS FILE EXISTS AT ALL: `GameContent` HAS NO TITLE FIELD, AND EVERY
 * SCREEN HAS BEEN RENDERING THE SLUG.
 *
 * `HomeScreen` passed `title={featured.id}` and `LibraryScreen` passed
 * `title={game.id}`, so a child has been shown *"sky-quest"* on the tile
 * they are meant to recognise. That is not a naming decision anybody made; it is
 * the absence of one, and it surfaces the moment a tile has to carry a name a
 * four-year-old reads with an adult.
 *
 * 🔴 THE NAMES ARE TRANSCRIBED FROM `games-spec.md` §2, NOT CHOSEN HERE. That
 * table is the source: *"Sky Quest"*, *"Letter Lagoon"*, *"Turo's
 * World"*, *"Kalqy World"*, *"Bridge Patterns"*. ⚠️ **OD-7 is open** — the
 * five are recorded as *"pond-world proposals"* with a default of *"as
 * proposed"*, and taking that default is exactly what this file does. Said out
 * loud because a default taken silently is how a wrong decision ships.
 *
 * ⚠️ IT LIVES IN THE UI LAYER RATHER THAN IN `shared/curriculum/`, AND THAT IS
 * DELIBERATE. The curriculum folder is loaded and asserted by
 * `check-no-mixed-language.mjs` and `check-audio-manifest.mjs`; a title is
 * neither content nor a voice line, and putting a display string through the
 * curriculum gates would mean the gates now police branding.
 *
 * ✅ IT MOVED HERE FROM `features/dashboards/kid/`, AND THE MOVE IS WHAT MADE IT
 * REACHABLE AT ALL. Sitting in a feature folder it was importable by nothing
 * outside that feature (`folderstructure.md`: *"`shared/` never imports from a
 * feature, and features never import each other's internals"*), so the four
 * adult tiers that now show per-game panels would each have had to invent their
 * own names for the same five games — which is the *"second vocabulary"* this
 * repo keeps refusing. Nothing imported it from the old path; it was written for
 * a caller that could not legally exist.
 */

/**
 * ⚠️ THERE USED TO BE A SECOND COLUMN HERE AND IT HELD THE ENGLISH NAME.
 *
 * No document in `docs/` ever gave a Malayalam name for any of the five, so the
 * bilingual table reported the English one rather than inventing a brand name
 * in a language nobody in the loop could check — which ENGINEERING-GUIDE.md §1 forbids, and
 * which is the kind of string that survives into a school's printed material.
 * ADR-044 removed the column and the question with it.
 *
 * ⚠️ OD-7 IS STILL OPEN on the English names themselves — these are transcribed
 * from `games-spec.md` §2, which records them as *"pond-world proposals"* with
 * a default of *"as proposed"*. Taking that default is what this file does.
 */
export const GAME_TITLES: Record<GameId, Localized<string>> = {
  "sky-quest": { en: "Sky Quest" },
  "letter-lagoon": { en: "Letter Lagoon" },
  "turos-world": { en: "Turo’s World" },
  "kalqy-world": { en: "Kalqy World" },
  "bridge-patterns": { en: "Bridge Patterns" },
};

export function gameTitle(id: GameId, lang: "en"): string {
  return GAME_TITLES[id][lang];
}

/**
 * How a school says the band out loud.
 *
 * 🔴 TRANSCRIBED FROM `org.prisma`'s OWN COMMENT, WHICH IS THE ONLY PLACE THIS
 * VOCABULARY IS RECORDED: *"'LKG', 'UKG' and 'Nursery' are grades in exactly the
 * schools this product is sold to"*. `AgeBand` is `BAND_3_4 | BAND_4_5 |
 * BAND_5_6` and a sidebar that printed those would be printing a database enum
 * at a child.
 *
 * ⚠️ THE ENGLISH LABELS PAIR THE GRADE WITH THE AGES rather than replacing them
 * — *"LKG · 4–5"*. A parent knows the ages, a teacher knows the grade, and both
 * read this screen over the child's shoulder.
 */
export const BAND_LABELS: Record<AgeBand, Localized<string>> = {
  BAND_3_4: { en: "Nursery · 3–4" },
  BAND_4_5: { en: "LKG · 4–5" },
  BAND_5_6: { en: "UKG · 5–6" },
};

/**
 * How hard the game asks the body to work — `GameContent.exertion`, spelled for
 * a human.
 *
 * ⚠️ IT IS NOT A DIFFICULTY RATING AND THE COPY MUST NEVER IMPLY ONE.
 * `types.ts` is emphatic that motor difficulty *"must be impossible to express
 * in the data"*, because *"a level-5 jump is the same jump as a level-1 jump"*.
 * Exertion describes how much a child MOVES — which is what an adult choosing a
 * game before nap time actually wants to know.
 */
export const EXERTION_LABELS: Record<
  GameContent["exertion"],
  Localized<string>
> = {
  low: { en: "Gentle" },
  medium: { en: "Some moving" },
  high: { en: "Lots of moving" },
};

/**
 * The tint each game's card and tile is drawn in.
 *
 * 🔴 IT IS THE GAME'S SKILL COLOUR FROM `design-tokens.css`, NOT A NEW PALETTE.
 * The token file already assigns one per content skill and names the game in the
 * comment — *"sunshine · Sky Quest"*, *"primary · Letter Lagoon"*. Using
 * anything else here would mean a game is one colour on its tile and another on
 * the skill bar directly below it.
 */
export const GAME_TINTS: Record<GameId, string> = {
  "sky-quest": "var(--color-skill-numeracy)",
  "letter-lagoon": "var(--color-skill-literacy)",
  "turos-world": "var(--color-skill-general-awareness)",
  "kalqy-world": "var(--color-skill-social-awareness)",
  "bridge-patterns": "var(--color-skill-matching)",
};
